import { t, type Idioma } from '@matias-asset-os/i18n';
import { fechaLarga } from '../componentes.js';
import { Dinero, PastillaEtapa, Progreso, ProximaAccion, Puntuacion } from './piezas.jsx';
import type { Obra } from './modelo.js';
import { ORDENES, type Orden } from './filtros.js';

/**
 * La lista: lo mismo que el mapa, en columnas ordenables.
 *
 * No es una vista secundaria. En el mapa se ve DONDE, y en la lista se ve EN
 * QUE ORDEN hay que atacarlo; para planificar una manana hace falta lo segundo.
 * Las dos beben del mismo filtrado, asi que nunca ensenan cosas distintas.
 */

interface Props {
  obras: Obra[];
  idioma: Idioma;
  hoy: string;
  seleccionada: string | null;
  orden: Orden;
  onOrden: (o: Orden) => void;
  onAbrir: (id: string) => void;
}

/**
 * Las columnas que ordenan, y por que criterio.
 *
 * Pulsar la cabecera es como se ordena una tabla desde siempre; el desplegable
 * de arriba hace lo mismo y ademas ordena el mapa. Los dos escriben en el mismo
 * estado, asi que no pueden discrepar.
 */
const COLUMNA_ORDENA: Partial<Record<string, Orden>> = {
  progreso: 'PROGRESO',
  fin: 'FINALIZACION',
  puntuacion: 'PUNTUACION',
  comprobacion: 'COMPROBACION',
  accion: 'ACCION',
  valor: 'VALOR',
};

function Cabecera({
  clave,
  texto,
  orden,
  onOrden,
}: {
  clave: keyof typeof COLUMNA_ORDENA | null;
  texto: string;
  orden: Orden;
  onOrden: (o: Orden) => void;
}) {
  const criterio = clave === null ? undefined : COLUMNA_ORDENA[clave];
  if (!criterio) return <th scope="col">{texto}</th>;
  return (
    <th scope="col" aria-sort={orden === criterio ? 'descending' : 'none'}>
      <button type="button" className="enlace col-ordena" onClick={() => onOrden(criterio)}>
        {texto}
        <span aria-hidden="true">{orden === criterio ? ' ▾' : ''}</span>
      </button>
    </th>
  );
}

/*
  La tabla NO lleva el selector de orden dentro. El orden se elige arriba, en la
  barra que comparten el mapa y la lista, porque tambien ordena el mapa: si
  viviera en la cabecera de la tabla, al pasar a mapa desapareceria el control
  de algo que sigue estando ordenado.
*/
export function Lista({ obras, idioma, hoy, seleccionada, orden, onOrden, onAbrir }: Props) {
  return (
    <div className="tarjeta plana tabla-scroll obras-lista">
      <table className="tabla">
        <thead>
          <tr>
            <Cabecera clave={null} texto={t(idioma, 'obras.colNombre')} orden={orden} onOrden={onOrden} />
            <Cabecera clave={null} texto={t(idioma, 'obras.zona')} orden={orden} onOrden={onOrden} />
            <Cabecera clave={null} texto={t(idioma, 'obras.fase')} orden={orden} onOrden={onOrden} />
            <Cabecera clave="progreso" texto={t(idioma, 'obras.progreso')} orden={orden} onOrden={onOrden} />
            <Cabecera clave="fin" texto={t(idioma, 'obras.colFin')} orden={orden} onOrden={onOrden} />
            <Cabecera clave={null} texto={t(idioma, 'obras.etapaComercial')} orden={orden} onOrden={onOrden} />
            <Cabecera clave="puntuacion" texto={t(idioma, 'obras.puntuacion')} orden={orden} onOrden={onOrden} />
            <Cabecera clave={null} texto={t(idioma, 'obras.responsable')} orden={orden} onOrden={onOrden} />
            <Cabecera clave="accion" texto={t(idioma, 'obras.proximaAccion')} orden={orden} onOrden={onOrden} />
            <Cabecera clave="comprobacion" texto={t(idioma, 'obras.ultimaComprobacion')} orden={orden} onOrden={onOrden} />
            <Cabecera clave="valor" texto={t(idioma, 'obras.valorMercado')} orden={orden} onOrden={onOrden} />
          </tr>
        </thead>
        <tbody>
          {obras.map((obra) => (
            <tr
              key={obra.id}
              className={obra.id === seleccionada ? 'fila-elegida' : undefined}
              onClick={() => onAbrir(obra.id)}
            >
              <td>
                <button type="button" className="enlace" onClick={() => onAbrir(obra.id)}>
                  {obra.nombre}
                </button>
                <small className="suave numerico"> {obra.codigo}</small>
              </td>
              <td>{obra.zona}</td>
              <td>{t(idioma, `obras.fase.${obra.fase}`)}</td>
              <td>
                <Progreso obra={obra} idioma={idioma} />
              </td>
              <td>{fechaLarga(obra.finalizacionEstimada, idioma)}</td>
              <td>
                <PastillaEtapa etapa={obra.etapa} idioma={idioma} />
              </td>
              <td>
                <Puntuacion obra={obra} idioma={idioma} hoy={hoy} compacta />
              </td>
              <td>{obra.responsable ?? <span className="suave">{t(idioma, 'obras.sinResponsable')}</span>}</td>
              <td>
                <ProximaAccion obra={obra} idioma={idioma} hoy={hoy} />
              </td>
              <td>{fechaLarga(obra.ultimaComprobacion, idioma)}</td>
              <td className="fin-num">
                <Dinero cents={obra.valorMercadoCents} idioma={idioma} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** El selector de orden. Fuera de la tabla para que sirva tambien al mapa. */
export function SelectorOrden({
  orden,
  idioma,
  onOrden,
}: {
  orden: Orden;
  idioma: Idioma;
  onOrden: (o: Orden) => void;
}) {
  return (
    <label className="campo campo-orden">
      <span className="etiqueta-campo">{t(idioma, 'obras.ordenar')}</span>
      <select value={orden} onChange={(e) => onOrden(e.target.value as Orden)}>
        {ORDENES.map((o) => (
          <option key={o} value={o}>
            {t(idioma, `obras.orden.${o}`)}
          </option>
        ))}
      </select>
    </label>
  );
}
